// Relatório de Ofertas — hub: cada oferta é um card; clicar abre o relatório
// específico: Cashback, Campanhas, Cupons de Desconto e Promoções.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Coins, CreditCard, Gift, Megaphone, Ticket } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const API = "/api";

export default function RelatorioOfertas() {
  const navigate = useNavigate();
  // undefined = ainda carregando (o selo pisca); null = não deu para ler.
  const [cashbackConfig, setCashbackConfig] = useState(undefined);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${API}/cashback/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setCashbackConfig(data))
      .catch(() => setCashbackConfig(null));
  }, []);

  // Cupons e Promoções saíram do "Em breve" em 17/09/2026 (features/relatorios-ofertas).
  const disponiveis = [
    {
      icon: CreditCard, cor: "text-sky-500", bg: "bg-sky-500/10", titulo: "Cartão Fidelidade", rota: "/admin/impulsione/relatorio-ofertas/cartao-fidelidade",
      e2e: "card-relatorio-cartao",
      desc: "Faturamento dos pedidos com carimbo, custo dos prêmios, retorno, se o cliente passou a voltar mais rápido e quem está perto de ganhar.",
    },
    {
      icon: Ticket, cor: "text-sky-500", bg: "bg-sky-500/10", titulo: "Cupons de Desconto", rota: "/admin/impulsione/relatorio-ofertas/cupons",
      e2e: "card-relatorio-cupons",
      desc: "Cada cupom: quando esteve disponível, quem usou, desconto aplicado, faturado, lucro e clientes novos.",
    },
    {
      icon: Gift, cor: "text-emerald-500", bg: "bg-emerald-500/10", titulo: "Promoções", rota: "/admin/impulsione/relatorio-ofertas/promocoes",
      e2e: "card-relatorio-promocoes",
      desc: "Quanto cada promoção vendeu, o desconto concedido e o lucro pela ficha técnica, produto a produto.",
    },
  ];
  const emBreve = [];

  return (
    <div className="p-4 sm:p-6">
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Relatório de Ofertas</h1>
          <p className="text-muted-foreground mt-1">
            Escolha uma oferta para ver o desempenho detalhado
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* CASHBACK — relatório disponível */}
          <button
            type="button"
            onClick={() => navigate("/admin/impulsione/relatorio-ofertas/cashback")}
            className="group text-left bg-card rounded-xl border-2 border-primary/30 p-6 shadow-md hover:shadow-lg hover:border-primary transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-500" strokeWidth={1.5} />
              </div>
              {cashbackConfig === undefined && <Skeleton className="h-6 w-24 rounded-full" />}
              {cashbackConfig && (
                <span
                  className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                    cashbackConfig.ativo
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {cashbackConfig.ativo
                    ? `Ativo · ${Number(cashbackConfig.percentual || 0).toLocaleString("pt-BR")}%`
                    : "Inativo"}
                </span>
              )}
            </div>
            <h3 className="font-semibold text-lg mb-1">Cashback</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Valor distribuído e usado, carteiras dos clientes e pedidos que
              consumiram cashback.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
              Ver relatório
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </span>
          </button>

          {/* CAMPANHAS — hub com a Campanha Inteligente */}
          <button
            type="button"
            onClick={() => navigate("/admin/impulsione/relatorio-ofertas/campanhas")}
            data-e2e="card-relatorio-campanhas"
            className="group text-left bg-card rounded-xl border-2 border-primary/30 p-6 shadow-md hover:shadow-lg hover:border-primary transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-purple-500" strokeWidth={1.5} />
              </div>
            </div>
            <h3 className="font-semibold text-lg mb-1">Campanhas</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Resultado das campanhas automáticas, começando pela Campanha Inteligente.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
              Ver relatórios
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </span>
          </button>

          {disponiveis.map(({ icon: Icon, cor, bg, titulo, rota, e2e, desc }) => (
            <button key={titulo} type="button" onClick={() => navigate(rota)} data-e2e={e2e}
              className="group text-left bg-card rounded-xl border-2 border-primary/30 p-6 shadow-md hover:shadow-lg hover:border-primary transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${bg} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 ${cor}`} strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="font-semibold text-lg mb-1">{titulo}</h3>
              <p className="text-sm text-muted-foreground mb-4">{desc}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Ver relatório
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          ))}

          {/* Futuros relatórios — em breve */}
          {emBreve.map((oferta) => {
            const Icon = oferta.icon;
            return (
              <div
                key={oferta.titulo}
                className="bg-card rounded-xl border p-6 opacity-70 cursor-not-allowed"
                title="Em breve"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${oferta.bg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${oferta.cor}`} strokeWidth={1.5} />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    Em breve
                  </span>
                </div>
                <h3 className="font-semibold text-lg mb-1">{oferta.titulo}</h3>
                <p className="text-sm text-muted-foreground">{oferta.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
